/**
 * Public-site chrome that used to be inline <script> blocks in _LayoutFrontend / ProductsIndex /
 * ProductsDetails (moved here so a Content-Security-Policy can drop 'unsafe-inline'):
 * - window.feText / window.pfText, now read from <script type="application/json"> data blocks;
 * - the GA4 bootstrap + the delegated contact-click listener (same rules as siteEvents.linkEventFor);
 * - the GLightbox gallery init.
 */
import { linkEventFor } from './siteEvents';

/** Parses a JSON data block (<script type="application/json" id=...>); null when absent or broken. */
export function readJsonBlock<T = unknown>(doc: Document, id: string): T | null {
    const el = doc.getElementById(id);
    if (!el || !el.textContent) return null;
    try {
        return JSON.parse(el.textContent) as T;
    } catch {
        return null;
    }
}

/** Publishes the server-rendered string maps on window, where the page scripts read them. */
export function exposeTextMaps(win: any, doc: Document): void {
    const fe = readJsonBlock<Record<string, string>>(doc, 'fe-text');
    if (fe) win.feText = fe;
    const pf = readJsonBlock<Record<string, string>>(doc, 'pf-text');
    if (pf) win.pfText = pf;
}

/** The GA4 measurement id the layout put in <meta name="sps-ga-id">, or null (no tag configured). */
export function gaMeasurementId(doc: Document): string | null {
    const id = doc.querySelector('meta[name="sps-ga-id"]')?.getAttribute('content')?.trim();
    return id ? id : null;
}

/** The standard gtag bootstrap: queue on dataLayer until gtag.js (loaded async) drains it. */
export function initAnalytics(win: any, measurementId: string): void {
    win.dataLayer = win.dataLayer || [];
    if (typeof win.gtag !== 'function')
        win.gtag = function gtag() { win.dataLayer.push(arguments); };
    win.gtag('js', new Date());
    win.gtag('config', measurementId);
}

/** One capture-phase listener: tel:/mailto:/WhatsApp link clicks become GA "Contact clicks". */
export function bindContactClicks(doc: Document, track: (name: string) => void): void {
    doc.addEventListener('click', (e) => {
        const target = e.target as Element | null;
        const a = target && typeof target.closest === 'function' ? target.closest('a[href]') : null;
        if (!a) return;
        const name = linkEventFor(a.getAttribute('href') || '');
        if (name) track(name);
    }, true);
}

/**
 * GLightbox over every .glightbox link, once. Skipped while a page still carries its own inline
 * initializer (ProductsDetails, until that script is removed) so a click never opens two lightboxes.
 */
export function initLightbox(win: any, doc: Document): boolean {
    if (win.__spsLightbox || typeof win.GLightbox !== 'function') return false;
    if (!doc.querySelector('.glightbox')) return false;
    const inline = Array.from(doc.querySelectorAll('script:not([src])'))
        .some(s => (s.textContent || '').includes('GLightbox('));
    if (inline) return false;
    win.__spsLightbox = win.GLightbox({ selector: '.glightbox' });
    return true;
}
